import { createContext, type PropsWithChildren, useContext, useMemo } from 'react';
import type { UseFormReturn } from 'react-hook-form';

import type { IndividualPaymentEditorViewModel } from '@/shared/api/generated';

import type { PlanFormValues } from '../hooks/usePlanFormSchema';

/** Modo de uso do formulário de plano. */
export type PlanFormMode = 'create' | 'edit';

/** Valor exposto pelo provider de edição de plano. */
export interface PlanEditContextValue {
  /**
   * Discriminante entre criar e editar.
   *
   * As seções do formulário são compartilhadas pelas duas telas, e algumas delas
   * dependem de um contrato que **ainda não existe** na criação: cupons e
   * documentos de convênio consomem endpoints com `financialPersonId` no path, a
   * auditoria não tem histórico para exibir e recriar parcelas não faz sentido
   * num plano cujas parcelas estão sendo geradas agora.
   *
   * O modo também decide de onde vêm as formas de pagamento: por contrato na
   * edição, por plano financeiro do curso na criação.
   */
  readonly mode: PlanFormMode;
  /** Formulário único das três abas — o `PUT` é um só. */
  readonly form: UseFormReturn<PlanFormValues>;
  /**
   * Resposta crua do editor.
   *
   * Necessária além do formulário por dois motivos: é a base do corpo do `PUT`
   * (campos não expostos voltam intactos) e é a fonte dos valores calculados
   * pelo backend, que a tela nunca recomputa.
   *
   * Sempre `undefined` no modo criação: não há recurso a carregar.
   */
  readonly editor: IndividualPaymentEditorViewModel | undefined;
  /** ID do contrato financeiro; `null` no modo criação. */
  readonly financialPersonId: number | null;
  /** `true` enquanto o `PUT` está em voo. */
  readonly isSaving: boolean;
  /** `true` quando o `PUT` foi recusado por permissão — a tela vira leitura. */
  readonly isReadOnly: boolean;
}

const PlanEditContext = createContext<PlanEditContextValue | null>(null);
PlanEditContext.displayName = 'PlanEditContext';

/**
 * Acessa o contexto de edição de plano.
 *
 * @returns Formulário, editor cru e estado de salvamento.
 * @throws Quando usado fora de `<PlanEditProvider>`.
 */
export function usePlanEdit(): PlanEditContextValue {
  const ctx = useContext(PlanEditContext);
  if (!ctx) throw new Error('usePlanEdit must be used within <PlanEditProvider>');
  return ctx;
}

/**
 * Provider do formulário de edição de plano.
 *
 * As abas consomem o mesmo `form` daqui em vez de receberem dezenas de props:
 * são três abas sobre um único payload, e passar `control` por prop em cada
 * nível tornaria a árvore ilegível sem ganhar nada.
 */
export function PlanEditProvider({
  children,
  mode,
  form,
  editor,
  financialPersonId,
  isSaving,
  isReadOnly,
}: PropsWithChildren<PlanEditContextValue>) {
  const value = useMemo(
    () => ({ mode, form, editor, financialPersonId, isSaving, isReadOnly }),
    [mode, form, editor, financialPersonId, isSaving, isReadOnly],
  );

  return <PlanEditContext.Provider value={value}>{children}</PlanEditContext.Provider>;
}
